import { createContext } from 'react';

import { PriceType } from '@cast/shared-types';
import { ClusterAnomaly, CostReportSummary, NoDataReason } from '@cast/types';

import {
  AggregatedMetrics,
  CostReportChartDataByPeriod,
  CostReportDataByPeriod,
} from '../types/costOverTime';

export type ComputeCostDynamicCopiesMap = {
  summary: {
    totalSpendTooltip: string;
    currentSpendTooltip: string;
    monthlyForecastTooltip: string;
    monthlyAverageTooltip: string;
    averageCpuCost: string;
  };
  computeSpend: {
    mainTitle: string;
    mainTooltip: string;
  };
  normalized: {
    mainDefaultTooltip: string;
    mainMonthlyTooltip: string;
    onDemandDefaultTooltip: string;
    onDemandMonthlyTooltip: string;
    fallbackDefaultTooltip: string;
    fallbackMonthlyTooltip: string;
    spotDefaultTooltip: string;
    spotMonthlyTooltip: string;
  };
  podCost?: {
    mainHourlyTooltip: string;
    mainDailyTooltip: string;
    mainMonthlyTooltip: string;
    onDemandHourlyTooltip: string;
    onDemandDailyTooltip: string;
    onDemandMonthlyTooltip: string;
    fallbackHourlyTooltip: string;
    fallbackDailyTooltip: string;
    fallbackMonthlyTooltip: string;
    spotHourlyTooltip: string;
    spotDailyTooltip: string;
    spotMonthlyTooltip: string;
  };
  podCount?: {
    average: string;
  };
};

type CostReportState = {
  isLoading: boolean;
  data: CostReportDataByPeriod[];
  chartData: CostReportChartDataByPeriod[];
  error?: unknown;
  summary?: CostReportSummary;
};

type CostOverTimeContextValue = {
  key: 'cluster' | 'workload' | 'namespace' | 'allocation-group';
  isCollecting: boolean;
  hasData: boolean;
  projectEndOfDayCost: boolean;
  applyUptime: boolean;
  /**
   * The actual granularity of cost metrics that come from the API.
   * For example:
   *   if hourly, we assume "cpuCostOnDemand" is the hourly cost,
   *   if daily, we assume "cpuCostOnDemand" is the daily cost.
   *
   * This is NOT related to "stepSeconds" that we send as part
   * of the request payload which determines the spacing of the data.
   *
   * As of the time of writing this, all reports EXCEPT Namespaces
   * return hourly costs.
   */
  timeSeriesStep: PriceType;
  /**
   * Storage cost is reported in either daily and hourly figures
   * depending on the report type.
   */
  storageCostGranularity: PriceType;
  error?: unknown;
  refetch?: () => void;
  current: CostReportState;
  previous: CostReportState;
  aggregatedMetrics: AggregatedMetrics;
  dynamicCopiesMap: ComputeCostDynamicCopiesMap;
  noDataReason?: NoDataReason;
  anomalies?: ClusterAnomaly[];
};

export const CostOverTimeContext = createContext<CostOverTimeContextValue>({
  key: '' as never,
  isCollecting: true,
  hasData: false,
  projectEndOfDayCost: true,
  applyUptime: true,
  timeSeriesStep: PriceType.DAILY,
  storageCostGranularity: PriceType.DAILY,
  current: {
    isLoading: true,
    data: [],
    chartData: [],
    summary: {} as never,
  },
  previous: {
    isLoading: true,
    data: [],
    chartData: [],
    summary: {} as never,
  },
  aggregatedMetrics: {} as never,
  dynamicCopiesMap: {} as never,
});
